import { lazy, Suspense, useCallback, useEffect, useState } from "react";

import { LEAD_SUBMITTED_EVENT, hasSubmittedLead, kitFromSlug, trackOfferViewed } from "@/lib/lead-magnets";
import type { LeadMagnet } from "@/lib/lead-magnets";

// Magnet 4: one offer card, once per visitor. This file is the trigger only;
// the card (and its form) loads when it is first shown.
const SlideInCard = lazy(() => import("./slide-in-card"));

const SHOWN_KEY = "aiia:lead-slide-in";
const REMEMBER_MS = 14 * 24 * 60 * 60 * 1000;
const EXIT_INTENT_AFTER_MS = 8_000;
const SCROLL_FRACTION = 0.6;

// The home page, the recruiter pages, pricing and the recruiter content hubs
// (interview questions, compare, industries, integrations), except the six
// pages the screening kit is built from (they already carry the kit).
// Everything else (the feature pages, which draw job seekers, tools, kits,
// sign in, the dashboard, the interview, booking, contact) never gets the card.
const ELIGIBLE = /^\/(?:$|pricing$|(?:recruiters|interview-questions|compare|industries|integrations)(?:\/|$))/;

const isEligible = (path: string): boolean =>
  ELIGIBLE.test(path) && !(path.startsWith("/interview-questions/") && kitFromSlug(path.split("/")[2]));

// Set once the card has been shown, so a blocked or full storage still stops
// it appearing on every page of one visit.
let shownThisVisit = false;

const shownRecently = (): boolean => {
  try {
    const at = Number(window.localStorage.getItem(SHOWN_KEY));
    return at > 0 && Date.now() - at < REMEMBER_MS;
  } catch {
    return false;
  }
};

const remember = (): void => {
  try {
    window.localStorage.setItem(SHOWN_KEY, String(Date.now()));
  } catch {
    // Storage can be blocked; shownThisVisit still covers this visit.
  }
};

// The cookie banner is open until the visitor chooses (and again when they
// reopen it from the footer). The card never competes with it.
const cookieBannerOpen = (): boolean => document.querySelector('section[data-consent-ui], [aria-label="Cookie preferences"]') !== null;

// The page's own offers, not the card's.
const offerBoxes = (): DOMRect[] =>
  Array.from(document.querySelectorAll(".lm-offer"))
    .filter((offer) => !offer.closest(".lm-slide-in"))
    .map((offer) => offer.getBoundingClientRect())
    .filter((box) => box.width > 0 && box.height > 0);

// An offer already on screen (even partly) is the offer; the card would only
// repeat it.
const offerInView = (): boolean => offerBoxes().some((box) => box.bottom > 0 && box.top < window.innerHeight);

// Scrolling down toward an offer: the visitor is about to reach it, so the
// card waits until they have scrolled past every offer on the page.
const offerAhead = (): boolean => offerBoxes().some((box) => box.bottom > 0);

export default function LeadSlideIn({ pathname }: { pathname: string }) {
  const path = pathname.replace(/\/+$/, "") || "/";
  const eligible = isEligible(path);
  const [shown, setShown] = useState(false);
  const close = useCallback(() => setShown(false), []);

  useEffect(() => {
    setShown(false);
    if (!eligible || shownThisVisit || shownRecently() || hasSubmittedLead()) return undefined;

    const startedAt = Date.now();
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

    const stop = () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onMouseOut);
    };
    // A blocked moment is skipped, not spent: the listeners stay, so the next
    // scroll or exit can still open the card.
    const show = (fromScroll: boolean) => {
      if (cookieBannerOpen() || hasSubmittedLead() || (fromScroll ? offerAhead() : offerInView())) return;
      stop();
      shownThisVisit = true;
      remember();
      setShown(true);
      trackOfferViewed("slide_in");
    };
    const onScroll = () => {
      const root = document.documentElement;
      const scrollable = root.scrollHeight - window.innerHeight;
      if (scrollable > 0 && window.scrollY / scrollable >= SCROLL_FRACTION) show(true);
    };
    // The pointer left through the top of the window, toward the tabs.
    const onMouseOut = (event: MouseEvent) => {
      if (!event.relatedTarget && event.clientY <= 0 && Date.now() - startedAt >= EXIT_INTENT_AFTER_MS) show(false);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    if (finePointer) document.addEventListener("mouseout", onMouseOut);
    return stop;
  }, [path, eligible]);

  // Another form sending an email makes the card pointless, and so does
  // scrolling back to an offer on the page. Its own form keeps the card open
  // to show the confirmation.
  useEffect(() => {
    if (!shown) return undefined;
    const onSubmitted = (event: Event) => {
      if ((event as CustomEvent<LeadMagnet>).detail !== "slide_in") close();
    };
    const onScroll = () => {
      if (offerInView()) close();
    };
    window.addEventListener(LEAD_SUBMITTED_EVENT, onSubmitted);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener(LEAD_SUBMITTED_EVENT, onSubmitted);
      window.removeEventListener("scroll", onScroll);
    };
  }, [shown, close]);

  if (!shown || !eligible) return null;
  return (
    <Suspense fallback={null}>
      <SlideInCard onClose={close} />
    </Suspense>
  );
}
